// catalogue-pricing.ts — purchase price reference of the catalogue (Settings > Catalogue screen).
//
// Pure helpers reusing only existing engine functions, never a formula duplicated in the UI.
// Never invent a value: an unknown material or standard print yields null, never a silent 0.

import { type QuoteLine, findMat, round2, num } from "../core/core";
import { ACTIVE_CATALOGUE, type Catalogue } from "./catalogue-source";
import { pvComponents, stdPrintRef } from "./base-price";
import { targetPaM2, combiComponents } from "../pricing/pa-margins";
import { catalogueFloor, materialFlatFee, componentFlatFees } from "../pricing/minima";
import type { VolumeGrid } from "../pricing/volume-tiers";

/**
 * Reference line built from a combination; no dimensions, since only per-m² PV and minima matter.
 * @param c The combination components.
 * @returns The reference line.
 */
const lineFrom = (c: { material: string; print: string; cut: string; lamination: string; finishing1: string; finishing2: string }): QuoteLine => ({
  type: "PRINT_GF", material: c.material, print: c.print, cut: c.cut, lamination: c.lamination, finishing1: c.finishing1, finishing2: c.finishing2,
});

/**
 * PV €/m² of a material's simple combination: material + standard print + straight cut (PV 0).
 * Basis of the Catalogue screen's target PA. Null when the material or standard print is unknown.
 * @param matRef The material reference.
 * @param cat The catalogue.
 * @returns The PV €/m², or null.
 */
export function pvCombiSimple(matRef: string, cat: Catalogue = ACTIVE_CATALOGUE): number | null {
  const materialEntry = findMat(matRef, cat);
  if (!materialEntry) return null;
  const print = stdPrintRef(materialEntry);
  if (!print || !cat.prints.some((p) => p.code === print)) return null; // never invent a value
  return round2(pvComponents({ type: "PRINT_GF", material: matRef, print }, cat)); // straight cut = 0 €/m²
}

/**
 * PV €/m² of a real combination (sum of its components). Null when the combination or material is unknown.
 * @param ref The combination reference.
 * @param cat The catalogue.
 * @returns The PV €/m², or null.
 */
export function pvCombi(ref: string, cat: Catalogue = ACTIVE_CATALOGUE): number | null {
  const c = combiComponents(ref);
  if (!c || !findMat(c.material, cat)) return null;
  return round2(pvComponents(lineFrom(c), cat));
}

/**
 * Effective minima of a combination, component cumuls included: min unit price and min flat fee.
 * @param ref The combination reference.
 * @param cat The catalogue.
 * @returns The minimum unit price and the minimum flat fee, or null.
 */
export function minimaCombi(ref: string, cat: Catalogue = ACTIVE_CATALOGUE): { minUnitPrice: number; minFlatFee: number } | null {
  const c = combiComponents(ref);
  if (!c || !findMat(c.material, cat)) return null;
  const l = lineFrom(c);
  const componentFlatFeesTotal = componentFlatFees(l, cat).reduce((s, f) => s + num(f.fee), 0);
  return { minUnitPrice: round2(catalogueFloor(l, undefined, cat)), minFlatFee: round2(materialFlatFee(l, cat) + componentFlatFeesTotal) };
}

/** Target PA of a volume tier: net tier PV × (1 − the tier's target margin). */
export interface TargetPaTier {
  sort_order: number;
  minThreshold: number;
  maxThreshold: number | null;
  /** Volume discount rate of the tier (0..1). */
  discountRate: number;
  /** Target margin of the tier (0..1). */
  targetMargin: number;
  /** Target PA €/m² = targetPaM2(pvBase, discountRate, targetMargin). */
  targetPa: number;
}

/**
 * Target PA per volume tier for a given base PV. The grid is the single source of rates and
 * target margins.
 * @param pvBase The base PV €/m².
 * @param grid The rate table.
 * @returns The target PA of each tier.
 */
export function targetPaTiers(pvBase: number, grid: VolumeGrid): TargetPaTier[] {
  return grid.tiers.map((p) => ({
    sort_order: p.sort_order,
    minThreshold: p.minThreshold,
    maxThreshold: p.maxThreshold,
    discountRate: p.discountRate,
    targetMargin: p.targetMargin,
    targetPa: round2(targetPaM2(pvBase, p.discountRate, p.targetMargin)),
  }));
}
